import React, { useState } from 'react';
import { raciMatrixData } from './data';
import './RaciMatrix.scss';

const RaciMatrix = () => {
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const roles = raciMatrixData.roles;
  const features = raciMatrixData.features;
  const categories = raciMatrixData.categories;

  const formatRoleName = (roleName) => {
    return roleName
      .replace('spclty_hub_', '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getCategoryClass = (category) => {
    const categoryMap = {
      'Specialty List Management': 'specialty-management',
      'Approval Workflows': 'approval-workflows',
      'Data Management': 'data-management',
      'Specialty & RC Management': 'specialty-rc',
      'Advanced Features': 'advanced'
    };
    return categoryMap[category] || 'default';
  };

  const filteredRoles = selectedRole 
    ? roles.filter(role => role.id === selectedRole)
    : roles;

  const filteredFeatures = selectedCategory
    ? features.filter(feature => feature.category === selectedCategory)
    : features;

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFeature(null);
  };

  return (
    <div className="raci-matrix-container">
      <header className="raci-header">
        <div className="raci-title-section">
          <h1>Role-Based Access Control Matrix</h1>
        </div>

        <div className="raci-controls">
          <div className="raci-filter-section">
            <label htmlFor="roleFilter">Filter by Role:</label>
            <select 
              id="roleFilter" 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {formatRoleName(role.name)}
                </option>
              ))}
            </select>
          </div>

          <div className="raci-filter-section">
            <label htmlFor="categoryFilter">Filter by Category:</label>
            <select 
              id="categoryFilter" 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <div className="raci-legend">
        <div className="raci-legend-item">
          <span className="raci-access-true">✓</span> = Has Access
        </div>
        <div className="raci-legend-item">
          <span className="raci-access-false">✗</span> = No Access
        </div>
        <div className="raci-legend-item">
          <span className="raci-clickable-hint">💡 Click features for details</span>
        </div>
        {(selectedRole || selectedCategory) && (
          <div className="raci-legend-item">
            <button 
              className="raci-clear-filters-btn"
              onClick={() => {
                setSelectedRole('');
                setSelectedCategory('');
              }}
            >
              ✕ Clear Filters
            </button>
          </div>
        )}
      </div>

      <div className="raci-table-wrapper">
        <table className="raci-matrix-table">
          <thead>
            <tr className="raci-header-row">
              <th className="raci-feature-header raci-sticky-column">
                <div className="raci-header-content">
                  <span>Feature</span>
                  <span className="raci-category-label">Category</span>
                </div>
              </th>
              {filteredRoles.map(role => (
                <th key={role.id} className="raci-role-header">
                  {formatRoleName(role.name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFeatures.map((feature, idx) => (
              <tr key={feature.id} className={idx % 2 === 0 ? 'raci-even' : 'raci-odd'}>
                <td 
                  className="raci-feature-cell raci-sticky-column raci-clickable"
                  onClick={() => handleFeatureClick(feature)}
                >
                  <div className="raci-feature-name">{feature.name}</div>
                  <div className={`raci-category-badge ${getCategoryClass(feature.category)}`}>
                    {feature.category}
                  </div>
                </td>
                {filteredRoles.map(role => (
                  <td key={role.id} className="raci-access-cell">
                    <span className={feature.permissions[role.id] ? 'raci-access-true' : 'raci-access-false'}>
                      {feature.permissions[role.id] ? '✓' : '✗'}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && selectedFeature && (
        <div className="raci-modal" style={{ display: 'block' }} onClick={closeModal}>
          <div className="raci-modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="raci-close" onClick={closeModal}>&times;</span>
            <h3>{selectedFeature.name}</h3>
            <p><strong>Category:</strong> {selectedFeature.category}</p>
            <p><strong>Description:</strong> {selectedFeature.description}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaciMatrix;
